import React, { useRef } from 'react'
import IsoDatePicker from '../../components/IsoDatePicker'
import { isIsoDate, type ISODate } from '../../lib/format'
import type { CellMove } from './CellEditor'

interface Props {
  value: ISODate | null
  onCommit: (v: string, move: CellMove) => void
  onCancel: () => void
}

/**
 * The `start` / `finish` cell (spec B §9.3): an `IsoDatePicker` whose popover is open as soon as
 * it mounts (double-click / Enter already decided to edit this cell) — there is no free-text
 * entry, so a selection is always a valid calendar date; `isIsoDate` stays as a defensive guard.
 * Closing the popover without picking a date cancels the edit.
 *
 * Fix round 1: the calendar's own keydowns (arrows to move the focused day, Enter to pick it,
 * Escape to dismiss) must never reach the grid's keyboard handler — `stopPropagation` on
 * `IsoDatePicker`'s `onKeyDown` (wired to both the trigger and the portaled popover content)
 * does that. Escape still cancels: Radix's own dismiss-on-Escape closes the popover regardless
 * (that logic lives on a native `document` listener, unrelated to this React-tree stop), which
 * fires the existing `onOpenChange(false)` below.
 */
const DateCell: React.FC<Props> = ({ value, onCommit, onCancel }) => {
  // Committing or cancelling unmounts this editor; guard against the popover's close notification
  // (which follows a selection too) re-invoking onCancel after onCommit already fired.
  const doneRef = useRef(false)
  // Review fix (parked minor): picking a day with Enter should advance the same way Enter does in
  // every other cell editor ('down'), not leave the grid focus on this cell ('stay'). The
  // underlying calendar (react-day-picker) only exposes this as "Enter was the last key pressed
  // before the day's click fired" — Tab isn't a day-selection gesture here (it just moves focus
  // off the popover, which Radix treats as dismissing it), so only Enter is tracked.
  const lastKeyRef = useRef<'Enter' | 'ShiftEnter' | null>(null)

  return (
    <IsoDatePicker
      size="sm"
      value={value}
      defaultOpen
      onKeyDown={(e) => {
        e.stopPropagation()
        // Task 3 review fix (Important #1): the two date columns must honour the "save and insert a
        // new activity below" shortcut like every `CellEditor`-based cell. Shift+Enter still picks
        // the focused day (a button click), so it is recorded here and turned into `'insert'` in
        // `onChange`; `Insert` picks nothing, so it commits an empty value — `commitCell` rejects it
        // (`isIsoDate('')` is false, no constraint is set) and only the row insertion happens.
        if (e.key === 'Insert') {
          e.preventDefault()
          if (doneRef.current) return
          doneRef.current = true
          onCommit('', 'insert')
          return
        }
        lastKeyRef.current = e.key === 'Enter' ? (e.shiftKey ? 'ShiftEnter' : 'Enter') : null
      }}
      onChange={(v) => {
        if (doneRef.current || !v || !isIsoDate(v)) return
        doneRef.current = true
        onCommit(v, lastKeyRef.current === 'ShiftEnter' ? 'insert' : lastKeyRef.current === 'Enter' ? 'down' : 'stay')
      }}
      onOpenChange={(open) => {
        if (open || doneRef.current) return
        doneRef.current = true
        onCancel()
      }}
    />
  )
}

export default DateCell
